class FeaturedCollectionTabsManager {
  static cache = new Map();
  static cacheExpiry = new Map();
  static CACHE_DURATION = 5 * 60 * 1000;

  static initializeSection(sectionId) {
    const sectionElement = document.querySelector(`#shopify-section-${sectionId}`);
    
    if (!sectionElement) return;
    
    if (sectionElement.hasAttribute('data-tabs-initialized')) {
      return;
    }
    
    sectionElement.setAttribute('data-tabs-initialized', 'true');
    
    class FeaturedCollectionTabs {
      constructor(element) {
        this.section = element;
        this.actualSectionId = this.section.id.replace('shopify-section-', '');
        this.sectionId = sectionId;
        this.loadingTabs = new Set();
        this.init();
      }
      
      init() {
        this.tabButtons = this.section.querySelectorAll('.tabs-nav__item');
        this.productList = this.section.querySelector('.product-list');
        this.bindEvents();
        this.setInitialActiveTab();
        this.preloadActiveCollections();
      }
      
      bindEvents() {
        this.tabButtons.forEach((button, index) => {
          button.addEventListener('click', (e) => {
            this.handleTabClick(e);
          });
          
          button.addEventListener('mouseenter', (e) => {
            this.preloadCollection(button.getAttribute('data-collection-url'));
          });
        });
      }
      
      setInitialActiveTab() {
        const defaultTab = this.section.querySelector('.tabs-nav__item[aria-expanded="true"]');
        if (defaultTab) {
          defaultTab.classList.add('is-active');
          this.updateButtonFromTabData();
        }
      }
      
      async preloadActiveCollections() {
        const visibleTabs = Array.from(this.tabButtons).slice(0, 3);
        for (const button of visibleTabs) {
          const collectionUrl = button.getAttribute('data-collection-url');
          if (collectionUrl) {
            this.preloadCollection(collectionUrl);
          }
        }
      }
      
      async preloadCollection(collectionUrl) {
        if (!collectionUrl) return;
        
        const cacheKey = `${collectionUrl}:${this.actualSectionId}`;
        
        if (this.getCachedData(cacheKey)) {
          return;
        }
        
        if (this.loadingTabs.has(cacheKey)) {
          return;
        }
        
        try {
          this.loadingTabs.add(cacheKey);
          await this.fetchAndCacheCollection(collectionUrl, cacheKey, true); // サイレント
        } catch (error) {
          console.warn(`Preload failed for ${collectionUrl}:`, error);
        } finally {
          this.loadingTabs.delete(cacheKey);
        }
      }
      
      async handleTabClick(event) {
        
        const button = event.target;
        const collectionUrl = button.getAttribute('data-collection-url');
        
        if (!collectionUrl) {
          return;
        }
        
        if (button.classList.contains('is-active')) {
          return;
        }
        
        this.updateActiveTab(button);
        this.updateButtonFromTabData();
        
        const cacheKey = `${collectionUrl}:${this.actualSectionId}`;
        
        const cachedData = this.getCachedData(cacheKey);
        if (cachedData) {
          this.updateProductList(cachedData);
          return;
        }
        
        this.showLoading();
        
        try {
          const data = await this.fetchAndCacheCollection(collectionUrl, cacheKey);
          if (data) {
            this.updateProductList(data);
          }
        } catch (error) {
          console.error(`コレクションの切り替えに失敗しました:`, error);
        } finally {
          this.hideLoading();
        }
        
      }
      
      updateButtonFromTabData() {
        const currentButtonLink = this.section.querySelector('.c_featured-collections__button');
        
        if (!currentButtonLink) {
          return;
        }
        
        const activeTab = this.section.querySelector('.tabs-nav__item.is-active');
        
        if (!activeTab) {
          return;
        }
        
        const buttonUrl = activeTab.getAttribute('data-button-url');
        const buttonText = activeTab.getAttribute('data-button-text');
        
        if (buttonUrl) {
          if (buttonUrl !== currentButtonLink.href) {
            currentButtonLink.href = buttonUrl;
          } else {
          }
        } else {
        }
        
        if (buttonText) {
          if (buttonText !== currentButtonLink.textContent.trim()) {
            currentButtonLink.textContent = buttonText;
          } else {
          }
        } else {
        }
      }
      
      getCachedData(cacheKey) {
        const cached = FeaturedCollectionTabsManager.cache.get(cacheKey);
        const expiry = FeaturedCollectionTabsManager.cacheExpiry.get(cacheKey);
        
        if (cached && expiry && Date.now() < expiry) {
          return cached;
        }
        
        if (cached) {
          FeaturedCollectionTabsManager.cache.delete(cacheKey);
          FeaturedCollectionTabsManager.cacheExpiry.delete(cacheKey);
        }
        
        return null;
      }
      
      async fetchAndCacheCollection(collectionUrl, cacheKey, silent = false) {
        const url = `${collectionUrl}?sections=${this.actualSectionId}`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        if (data[this.actualSectionId]) {
          FeaturedCollectionTabsManager.cache.set(cacheKey, data[this.actualSectionId]);
          FeaturedCollectionTabsManager.cacheExpiry.set(
            cacheKey, 
            Date.now() + FeaturedCollectionTabsManager.CACHE_DURATION
          );
          
          return data[this.actualSectionId];
        }
        
        throw new Error(`Section data not found for ${this.actualSectionId}`);
      }
      
      updateProductList(newSectionHtml) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newSectionHtml;
        
        const newProductListInner = tempDiv.querySelector('.product-list__inner');
        const currentProductListInner = this.section.querySelector('.product-list__inner');
        
        if (newProductListInner && currentProductListInner) {
          this.animateProductListUpdate(currentProductListInner, newProductListInner);
          this.updateNavigationButtons(tempDiv);
          
          this.updateButtonFromServerResponse(tempDiv);
          
          setTimeout(() => {
            this.updateButtonFromTabData();
          }, 100);
        }
      }
      
      updateButtonFromServerResponse(newSectionElement) {
        
        const newButtonLink = newSectionElement.querySelector('.c_featured-collections__button');
        const currentButtonLink = this.section.querySelector('.c_featured-collections__button');
        
        const activeTab = this.section.querySelector('.tabs-nav__item.is-active');
        const hasTabData = activeTab && 
          (activeTab.getAttribute('data-button-url') || activeTab.getAttribute('data-button-text'));
        
        if (hasTabData) {
          return;
        }
        
        if (newButtonLink && currentButtonLink) {
          const newHref = newButtonLink.href;
          const currentHref = currentButtonLink.href;
          
          if (newHref !== currentHref) {
            currentButtonLink.href = newHref;
          }
          
          const newText = newButtonLink.textContent.trim();
          const currentText = currentButtonLink.textContent.trim();
          
          if (newText !== currentText && newText) {
            currentButtonLink.textContent = newText;
          }
        } else if (newButtonLink && !currentButtonLink) {
          const buttonWrapper = newSectionElement.querySelector('.c_button__link--wrapper');
          const productList = this.section.querySelector('.product-list');
          
          if (buttonWrapper && productList) {
            productList.appendChild(buttonWrapper.cloneNode(true));
          }
        } else if (!newButtonLink && currentButtonLink) {
          const currentButtonWrapper = this.section.querySelector('.c_button__link--wrapper');
          if (currentButtonWrapper) {
            currentButtonWrapper.remove();
          }
        }
      }
      
      animateProductListUpdate(currentElement, newElement) {
        currentElement.style.transition = 'opacity 0.2s ease';
        currentElement.style.opacity = '0';
        
        setTimeout(() => {
          currentElement.innerHTML = newElement.innerHTML;
          currentElement.style.opacity = '1';
          
          setTimeout(() => {
            currentElement.style.transition = '';
          }, 400);
        }, 400);
      }
      
      updateActiveTab(activeButton) {
        this.tabButtons.forEach(btn => {
          btn.setAttribute('aria-expanded', 'false');
          btn.classList.remove('is-active');
        });
        
        activeButton.setAttribute('aria-expanded', 'true');
        activeButton.classList.add('is-active');
      }
      
      showLoading() {
        if (!this.productList) return;
        
        this.hideLoading();
        
        const loadingOverlay = document.createElement('div');
        loadingOverlay.classList.add('loading-overlay');
        
        const circle = document.createElement('div');
        circle.classList.add('loading-circle');
        
        loadingOverlay.appendChild(circle);
        
        const originalPosition = this.productList.style.position;
        if (!originalPosition || originalPosition === 'static') {
          this.productList.style.position = 'relative';
        }
        
        this.productList.appendChild(loadingOverlay);
      }
      
      hideLoading() {
        if (!this.productList) return;
        
        const loadingOverlay = this.productList.querySelector('.loading-overlay');
        if (loadingOverlay) {
          loadingOverlay.remove();
        }
      }
      
      updateNavigationButtons(newSectionElement) {
        const newNavButtons = newSectionElement.querySelector('.product-list__prev-next');
        const currentNavButtons = this.section.querySelector('.product-list__prev-next');
        
        if (newNavButtons && currentNavButtons) {
          currentNavButtons.replaceWith(newNavButtons.cloneNode(true));
        }
      }
      
      destroy() {
        this.tabButtons.forEach(button => {
          button.removeEventListener('click', this.handleTabClick);
          button.removeEventListener('mouseenter', this.preloadCollection);
        });
      }
    }
    
    function initializeSection() {
      new FeaturedCollectionTabs(sectionElement);
    }
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeSection);
    } else {
      initializeSection();
    }
  }
  
  static clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }
  
  static getCacheStats() {
    const now = Date.now();
    const validEntries = Array.from(this.cacheExpiry.entries())
      .filter(([key, expiry]) => expiry > now);
    
    return {
      totalEntries: this.cache.size,
      validEntries: validEntries.length,
      expiredEntries: this.cache.size - validEntries.length
    };
  }
}